import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/auth-context';
import { BRAND_NAME, SUPPORT_EMAIL } from '@/config/brand';
import { isNative } from '@/platform/native';
import { subscriptionRequiredRefusal } from '@/lib/subscription-error-handler';
import type { ReadOnlyRefusal } from '@/lib/billing-events';

const REASON_TEXT: Record<string, string> = {
  trial_ended: 'trialEnded',
  payment_lapsed: 'paymentLapsed',
  cancelled: 'cancelled',
  account_on_hold: 'onHold',
};

/**
 * English defaults (the server's own sentences): the toast can fire on a route whose first paint did not load the
 * subscription namespace, and must never show a raw key.
 */
export const READ_ONLY_DEFAULTS = {
  title: 'Your account is read-only',
  choosePlan: 'Choose a plan',
  trialEnded: 'Your free trial has ended. Choose a plan to keep creating and sending.',
  paymentLapsed: 'Your payment did not go through. Update your card to keep creating and sending.',
  cancelled: 'Your subscription has ended. Choose a plan to keep creating and sending.',
  onHold: 'This account is paused by {{brand}} support, so changes are paused. Contact {{supportEmail}} to restore it.',
  generic: 'Your account is read-only. Choose a plan to keep creating and sending.',
  crew: 'This account is read-only. Ask the owner to choose a plan.',
  notSaved: 'Not saved: your account is read-only',
} as const;

/** The app's only read-only sentence (`billing.native.readOnly`): no plan, no way to buy. */
const NATIVE_READ_ONLY_DEFAULT = 'This account is read-only. You can view everything, but not create or send.';

/**
 * The signed-in role, or undefined outside an AuthProvider: leaf components (send sheet, client form) only adapt
 * their wording to it. useAuth always calls useContext before it throws, so the hook order never changes.
 */
function useRole(): string | undefined {
  try {
    return useAuth().user?.role;
  } catch {
    return undefined;
  }
}

/**
 * The sentence that tells the user why a write was refused with 403 `subscription_required`, in their language.
 * Owners get the reason; crew and staff cannot choose a plan, so they are told to ask the owner. An unknown
 * reason falls back to the server's own message.
 */
export function useReadOnlyMessage() {
  // No suspense: this also runs above the routes (ReadOnlyRefusalToast), where suspending would blank the app.
  const { t } = useTranslation('subscription', { useSuspense: false });
  const isAdmin = useRole() === 'admin';

  const forRefusal = useCallback(
    (refusal: ReadOnlyRefusal | null | undefined): string => {
      // A platform hold is not a billing state: nobody can fix it with a plan, so crew and owners read the same text.
      if (refusal?.reason === 'account_on_hold') return t('billing.refused.onHold', { defaultValue: READ_ONLY_DEFAULTS.onHold, brand: BRAND_NAME, supportEmail: SUPPORT_EMAIL });
      // The app names no plan and no way to buy one (store policy): the same plain sentence for every role.
      if (isNative()) return t('billing.native.readOnly', NATIVE_READ_ONLY_DEFAULT);
      if (!isAdmin) return t('billing.refused.crew', READ_ONLY_DEFAULTS.crew);
      const key = refusal?.reason ? (REASON_TEXT[refusal.reason] as keyof typeof READ_ONLY_DEFAULTS | undefined) : undefined;
      if (key) return t(`billing.refused.${key}`, READ_ONLY_DEFAULTS[key]);
      return refusal?.message || t('billing.refused.generic', READ_ONLY_DEFAULTS.generic);
    },
    [isAdmin, t],
  );

  /** null when the error is not a read-only refusal. */
  const forError = useCallback(
    (error: unknown): string | null => {
      const refusal = subscriptionRequiredRefusal(error);
      return refusal ? forRefusal(refusal) : null;
    },
    [forRefusal],
  );

  /** The autosave label of an editor whose changes the server refused. */
  const notSaved = t('billing.refused.notSaved', READ_ONLY_DEFAULTS.notSaved);

  return { forRefusal, forError, isAdmin, notSaved };
}
